import React, { Component } from "react";
import { Dropdown } from "semantic-ui-react";
import { withTranslation } from "react-i18next";

import "./BrandGroupsFilter.css";

const GROUP_VALUE_PREFIX = "group-";

const toGroupValue = groupId => `${GROUP_VALUE_PREFIX}${groupId}`;
const isGroupValue = value => typeof value === "string" && value.startsWith(GROUP_VALUE_PREFIX);

const sortByName = (a, b) => a.name.localeCompare(b.name);

const createBrandDropdownOptions = (brands, className) =>
  brands.map(brand => ({
    key: brand.vin_prefix,
    value: brand.vin_prefix,
    text: `${brand.name} - ${brand.vin_prefix}`,
    className,
  }));

const isGroupSelected = (groupVins, selectedVin) => groupVins.length > 0 && groupVins.every(vin => selectedVin.has(vin));

const searchGroupedBrands = (options, query) => {
  const search = query.toLowerCase();
  const matches = option => typeof option.text === "string" && option.text.toLowerCase().includes(search);

  const filteredOptions = [];
  let pendingGroupHeader = null;

  options.forEach(option => {
    const isMatch = matches(option);

    if (isGroupValue(option.value)) {
      pendingGroupHeader = isMatch ? null : option;
      if (isMatch) filteredOptions.push(option);
      return;
    }

    if (!isMatch) return;

    if (pendingGroupHeader) {
      filteredOptions.push(pendingGroupHeader);
      pendingGroupHeader = null;
    }
    filteredOptions.push(option);
  });

  return filteredOptions;
};

class BrandGroupsFilter extends Component {
  state = {
    selectedGroupValues: [],
  };

  getGroupVinByValue = () => {
    const map = new Map();
    (this.props.brandGroups || []).forEach(group => {
      map.set(
        toGroupValue(group.id),
        (group.brands || []).map(brand => brand.vin_prefix)
      );
    });
    return map;
  };

  getGroupedOptions = () => {
    const groupOptions = [...(this.props.brandGroups || [])].sort(sortByName).flatMap(group => {
      const groupBrands = [...(group.brands || [])].sort(sortByName);
      if (!groupBrands.length) return [];

      return [
        { key: toGroupValue(group.id), value: toGroupValue(group.id), text: group.name, className: "brand-group-header" },
        ...createBrandDropdownOptions(groupBrands, "brand-group-item"),
      ];
    });
    const ungroupedOptions = createBrandDropdownOptions((this.props.brands || []).filter(brand => !brand.brand_group_id).sort(sortByName));

    return [...groupOptions, ...ungroupedOptions];
  };

  getSelectedValue = (groupVinByValue, selectedVin) => {
    const activeGroupValues = this.state.selectedGroupValues.filter(groupValue => isGroupSelected(groupVinByValue.get(groupValue) || [], selectedVin));
    const collapsedVinPrefixes = new Set(activeGroupValues.flatMap(groupValue => groupVinByValue.get(groupValue) || []));

    return [...activeGroupValues, ...this.props.value.filter(vin => !collapsedVinPrefixes.has(vin))];
  };

  getOptions = (groupVinByValue, selectedValue, selectedVin) => {
    const collapsedGroupValues = new Set(selectedValue.filter(isGroupValue));
    const collapsedVinPrefixes = new Set([...collapsedGroupValues].flatMap(groupValue => groupVinByValue.get(groupValue) || []));

    return this.getGroupedOptions().filter(option =>
      isGroupValue(option.value) ? collapsedGroupValues.has(option.value) || !isGroupSelected(groupVinByValue.get(option.value) || [], selectedVin) : !collapsedVinPrefixes.has(option.value)
    );
  };

  handleChange = (_, { value: selectedValues }) => {
    const groupVinByValue = this.getGroupVinByValue();
    this.setState({ selectedGroupValues: selectedValues.filter(isGroupValue) });

    const selectedVin = selectedValues.flatMap(value => groupVinByValue.get(value) || [value]);
    this.props.onChange([...new Set(selectedVin)]);
  };

  renderLabel = option => ({
    content: option.text,
    className: isGroupValue(option.value) ? "brand-group-label" : undefined,
  });

  render() {
    const groupVinByValue = this.getGroupVinByValue();
    const selectedVin = new Set(this.props.value);
    const selectedValue = this.getSelectedValue(groupVinByValue, selectedVin);

    return (
      <Dropdown
        className="BrandGroupsFilter"
        fluid
        selection
        multiple
        clearable
        search={searchGroupedBrands}
        value={selectedValue}
        options={this.getOptions(groupVinByValue, selectedValue, selectedVin)}
        placeholder={this.props.t("groups_brands").message || "Groups / Brands"}
        onChange={this.handleChange}
        renderLabel={this.renderLabel}
      />
    );
  }
}

export default withTranslation()(BrandGroupsFilter);
